import { useState, useEffect, useMemo, ReactElement, useCallback, JSXElementConstructor } from 'react';
import store, { loanActions } from '@apply/adminStore';
import { DrawHeader } from './headers/DrawHeader';
import { DrawApi } from '@apply/api';
import { DrawColumn } from './columns/DrawColumn';
import { useLocation } from 'react-router-dom';
import { DrawType, LoanStatusType, FeatureType } from '@apply/constants';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { DrawGrid } from './DrawGrid';
import React from 'react';
import DataGrid from 'devextreme-react/data-grid';
import { BfiSnackbar, LoadingModal, isMobileScreen, detectDeviceType, detectScreenWidth } from '@apply/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { curvatureFactor, iosLinearGrowthRate, linearGrowthRate } from './constants';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { DrawTotalItems } from './totalItems/DrawTotalItems.js';
import { DrawBudgetCategory } from './functions/index.js';
import { FeedbackModal } from '../draws/modals/FeedbackModal';
import { FeedbackModalV2 } from '../draws/modals/modalsv2/FeedbackModalV2';

import { DrawButtonGroup } from './DrawButtonGroup.js';

import { useDrawTableStore } from './useDrawTableStore.js';
import { snackbarWarning } from '../../../../../../apps/admin/src/common/functions/snackbarFunctions.js';
import { useAuthentication } from '../../../../../../apps/bfi-apply/src/app/Login/hooks/useAuthentication';
import { useDenyGCAccessIfUnauthorized } from '../../../../../../apps/bfi-apply/src/app/draws_v2/functions/gcDrawAuthorization.js';
import { BorderStyle, Height } from '@material-ui/icons';
import { Panel, PanelBorderStyle, PanelTheme } from '@apply/components';

interface IDrawTableParameters {
    applicationInsights: ApplicationInsights;
    borrowerName: string;
    isAdmin?: boolean;
    isInspectionsEnabled?: boolean;
}

export interface MobileScrollData {
    largeScreen: boolean;
    columnWidth: (width: number) => number;
    acquisitionOffset: number;
    secondColumnIndex: number;
    thirdColumnIndex: number;
    preparedMobileColumns: (ReactElement<any, string | JSXElementConstructor<any>> | null)[] | undefined;
}

export const DrawTable = ({
    applicationInsights,
    borrowerName,
    isAdmin = false,
    isInspectionsEnabled = false,
}: IDrawTableParameters): ReactElement => {
    //Used for Apply side only validation of a feature - Draws is a shared control
    const { isFeatureEnabled, authentication } = useAuthentication();

    const checkApplyV2Enabled = () => {
        if (isAdmin) {
            return false;
        } else {
            return isFeatureEnabled(FeatureType.ApplyV2);
        }
    };

    const location = useLocation();
    const { dispatch } = store;
    const [originPage, set_originPage] = useState('');
    const [originSection, set_originSection] = useState('');
    const [backButtonText, set_backButtonText] = useState('');
    const drawId = parseInt(location.pathname.split('/')[2]);
    const mobileScreen = isMobileScreen();

    const dataGridRef = useMemo(() => React.createRef<DataGrid>(), []);

    const toggleArray = useDrawTableStore((s) => s.toggleArray);
    const rset_toggleArray = useDrawTableStore((s) => s.set_toggleArray);

    const drawApi = useMemo(() => new DrawApi.DrawApi(applicationInsights), [applicationInsights]);

    const [drawBudget, set_drawBudget] = useState<DrawApi.IDrawBudget | undefined>();
    const [categories, set_categories] = useState<DrawBudgetCategory[]>([]);
    const [drawColumns, set_drawColumns] = useState<
        ReactElement<any, string | React.JSXElementConstructor<any>>[][][] | undefined
    >();
    const [drawTotalItems, set_drawTotalItems] = useState<
        ReactElement<any, string | React.JSXElementConstructor<any>>[][][] | undefined
    >();
    const [isEquityMet, set_isEquityMet] = useState<boolean>(false);
    const [equityAmount, set_equityAmount] = useState<number>(0);
    const [totalFunded, set_totalFunded] = useState<number>(0);
    const [latestDrawBorrowerRequestTotal, set_latestDrawBorrowerRequestTotal] = useState<number>(0);
    const [latestDrawFundedTotal, set_latestDrawFundedTotal] = useState<number>(0);
    const [latestDrawCreatedDate, set_latestDrawCreatedDate] = useState<Date>();
    const [isLoading, set_isLoading] = useState<boolean>(false);
    const [isSnackbarOpen, set_isSnackbarOpen] = useState<boolean>(false);
    const [snackbarMessage, set_snackbarMessage] = useState<string>('');
    const [isFeedbackOpen, set_isFeedbackOpen] = useState<boolean>(false);
    const [isReadOnly, set_isReadOnly] = useState<boolean>(false);

    const anyAcquisitionAmount: boolean = useMemo(
        () => (drawBudget?.drawBudgetItems?.reduce((sum, item) => (sum += item?.acquisitionAmount ?? 0), 0) ?? 0) > 0,
        [drawBudget?.drawBudgetItems]
    );

    // Used to position the mobile chevrons to the center of a column
    const chevronsCenteredOffset = 18;
    const largeScreen = !mobileScreen;
    const showMobileScroll = !isAdmin && !largeScreen;
    const acquisitionOffset = anyAcquisitionAmount ? 5 : 4;

    const finalizeSnackbar = (notFinalized: boolean) => {
        if (notFinalized) snackbarWarning('Please click "Finalize" to complete Draw');
    };

    // Mobile scroll state
    // Start at the AVAILABLE column for the second column
    // Start at the last DRAW for the third column
    const [secondColumnIndex, set_secondColumnIndex] = useState(0);
    const [thirdColumnIndex, set_thirdColumnIndex] = useState(0);
    const [numberOfDraws, set_numberOfDraws] = useState(0);

    // Mobile scroll logic governing the second and third columns of the mobile view
    const handleNextSecond = () => {
        set_secondColumnIndex((prev) => (prev + 1) % (acquisitionOffset + 1) || 1);
    };

    const handlePreviousSecond = () => {
        set_secondColumnIndex((prev) => (prev - 1 + (acquisitionOffset + 1)) % (acquisitionOffset + 1) || acquisitionOffset);
    };

    const handleNextThird = () => {
        set_thirdColumnIndex((prev) => (prev + 1) % (numberOfDraws + acquisitionOffset + 1) || acquisitionOffset + 1);
    };

    const handlePreviousThird = () => {
        set_thirdColumnIndex((prev) => (prev - 1 < acquisitionOffset + 1 ? acquisitionOffset + numberOfDraws : prev - 1));
    };

    // Adjusting for mobile screen size
    // This function converts the desktop columns to the appropriate size for mobile
    // The exception is that the blank column in the desktop view is used for the DESCRIPTION
    // column in the mobile view thus the function call: columnWidth(20)
    const columnWidth = (columnWidth: number) => {
        const screenWidth = detectScreenWidth();
        if (!mobileScreen) return columnWidth;

        // Using a formula to calculate the width of the DESCRIPTION column based on the mobile viewport width
        // (this is the same width as the draw-footer-double-cell) BFIA-4098
        const agnosticColumnWidth =
            curvatureFactor * screenWidth ** 2 +
            (detectDeviceType() === 'ios' ? iosLinearGrowthRate : linearGrowthRate) * screenWidth;

        switch (columnWidth) {
            case 20:
                return agnosticColumnWidth;
            case 200:
            case 100:
                return 90;
            default:
                return columnWidth;
        }
    };

    // Pre-clone the drawColumns array and apply the `visible` logic to the nested component
    // so as to not clutter the JSX or tightly couple the logic
    const preparedMobileColumns = useMemo(() => {
        return drawColumns?.map((col, index) => {
            if (React.isValidElement([col][0][0][0])) {
                return React.cloneElement([col][0][0][0], {
                    visible: thirdColumnIndex === index + acquisitionOffset + 1,
                });
            } else {
                return null;
            }
        });
    }, [drawColumns, thirdColumnIndex]);

    const mobileScrollData: MobileScrollData = useMemo(
        () => ({
            largeScreen: largeScreen,
            columnWidth: columnWidth,
            acquisitionOffset: acquisitionOffset,
            secondColumnIndex: secondColumnIndex,
            thirdColumnIndex: thirdColumnIndex,
            preparedMobileColumns: preparedMobileColumns,
        }),
        [largeScreen, acquisitionOffset, secondColumnIndex, thirdColumnIndex, preparedMobileColumns]
    );

    const latestDrawHasFunding: boolean = useMemo(() => latestDrawFundedTotal > 0, [latestDrawFundedTotal]);

    useEffect(() => {
        const isGeneralContractor = authentication?.user?.isGeneralContractor ?? false;

        const drawColumns = drawBudget?.draws?.map((draw) =>
            DrawColumn({
                applicationInsights,
                drawId: draw.id,
                drawNumber: draw.drawNumber,
                drawType: draw.drawType,
                drawFinalized: draw.isFinalized,
                latestDrawHasFunding,
                hasResubmitted: draw.hasResubmitted,
                needsBorrowerSignature: draw.needsBorrowerSignature,
                hasGeneralContractorSignature: draw.hasGeneralContractorSignature ?? false,
                isGeneralContractor,
                isInspectionRequireFirst: draw.isInspectionRequireFirst,
                borrowerSubmitted: draw.borrowerSubmitted,
                fundedOn: draw.fundedOn,
                toggleArray,
                set_drawBudget,
                isAdmin,
                set_isSnackbarOpen,
                set_snackbarMessage,
                set_isLoading,
                isReadOnly,
            })
        );
        set_drawColumns(drawColumns);
        set_numberOfDraws(drawColumns?.length ?? 0);
        set_secondColumnIndex(acquisitionOffset);
        set_thirdColumnIndex((drawColumns?.length ?? 0) + acquisitionOffset);

        const totalItems = drawBudget?.draws?.map((draw) =>
            DrawTotalItems({
                drawFinalized: draw.isFinalized,
                drawId: draw.id,
                drawNumber: draw.drawNumber,
                drawType: draw.drawType,
                hasResubmitted: draw.hasResubmitted,
                isAdmin,
                isInspectionRequireFirst: draw.isInspectionRequireFirst,
                needsBorrowerSignature: draw.needsBorrowerSignature,
                toggleArray,
            })
        );

        set_drawTotalItems(totalItems);
    }, [drawBudget, toggleArray, latestDrawHasFunding]);

    const noDraws = (drawBudget?.draws?.length ?? 1) === 0;

    const retrieveOriginPage = () => {
        const search = window.location.search;
        const params = new URLSearchParams(search);
        const currentOriginPage = params.get('originPage');
        const currentOriginSection = params.get('originSection');
        set_originPage(currentOriginPage ? currentOriginPage : '');
        set_originSection(currentOriginSection ? currentOriginSection : '');
    };

    useEffect(() => {
        const backButtonPrefix = 'Back to ';
        switch (originPage) {
            case 'dashboard':
                set_backButtonText(backButtonPrefix + 'Dashboard');
                break;
            case 'approved':
                set_backButtonText(backButtonPrefix + 'Approved');
                break;
            case 'outstanding':
                set_backButtonText(backButtonPrefix + 'Outstanding');
                break;
            case 'repaid':
                set_backButtonText(backButtonPrefix + 'Repaid');
                break;
            default:
                set_backButtonText(backButtonPrefix + 'Loan');
                break;
        }
    }, [originPage]);

    useEffect(() => {
        const loadDraw = async () => {
            set_isLoading(true);
            const response = await drawApi.getDrawBudget(drawId);
            set_isLoading(false);

            if (response.statusCode === 401) {
                let redirectURL = '/';
                if (!isAdmin) {
                    redirectURL = `/login?redirectTo=${location.pathname}`;
                }
                window.location.href = redirectURL;
            }
            const drawBudget = response.result;
            const draws = drawBudget?.draws;
            if (draws?.length) {
                const latestDraw = draws[draws?.length - 1];
                if (
                    !latestDraw?.isFinalized &&
                    latestDraw?.borrowerRequested &&
                    latestDraw?.inspectionOrdered &&
                    latestDraw?.inspectedOn &&
                    latestDraw?.fundedOn
                )
                    finalizeSnackbar(true);
            }

            set_drawBudget(drawBudget);

            set_isReadOnly(![LoanStatusType.Outstanding, LoanStatusType.Approved].includes(drawBudget.loanStatusId));
        };

        loadDraw();
        retrieveOriginPage();
    }, [isAdmin, drawId, drawApi]);

    const denyGCAccess = useDenyGCAccessIfUnauthorized(drawBudget?.canGCAddDraw);

    useEffect(() => {
        if (!drawBudget) return;
        if (!isAdmin && denyGCAccess) {
            window.location.href = '/home';
        }
    }, [drawBudget, denyGCAccess, isAdmin]);

    useEffect(() => {
        if (!drawBudget) return;

        if (toggleArray?.length === drawBudget?.drawCount) return;

        const newArray: boolean[] = [];
        for (let i = 0; i < drawBudget.drawCount; i++) {
            newArray.push(false);
        }
        rset_toggleArray(newArray);
    }, [dispatch, drawBudget, toggleArray]);

    const set_currentApproval = useCallback(
        (currentApproval: DrawApi.IDrawBudgetApproval | undefined) => {
            if (!drawBudget) return;

            const editedDrawBudget: DrawApi.IDrawBudget | undefined = { ...drawBudget, currentApproval };
            set_drawBudget(editedDrawBudget);
        },
        [drawBudget?.id]
    );

    const calculateGridWidth = useCallback(() => {
        const baseDesktopWidth = 652; // Summed total of columns and leftmost icon column

        // Maintaining the grid width to screen size ratio for a consistent mobile view
        // This also removes the blank column on the left of the mobile view for the Budget Grid
        let width = isMobileScreen() ? detectScreenWidth() * 0.34 : baseDesktopWidth;

        const extraAdminColumns = 300;
        const acquisitionColumn = 100;
        const expandedAutomaticColumn = 500;
        const expandedManualColumn = 600;

        if (isAdmin) {
            width += extraAdminColumns;
        } else {
            if (anyAcquisitionAmount) {
                width += acquisitionColumn;
            }
        }

        if (!drawBudget?.draws) return width;

        width += drawBudget.draws ? drawBudget.draws.length * 100 : 0;

        const toggledIndex = toggleArray?.indexOf(true);

        if (drawBudget.draws && toggledIndex >= 0 && drawBudget.draws[toggledIndex]?.drawType) {
            if (drawBudget.draws[toggledIndex]?.drawType === DrawType.Automatic) {
                width += expandedAutomaticColumn;
            }

            if (drawBudget.draws[toggledIndex]?.drawType === DrawType.Manual) {
                width += expandedManualColumn;
            }
        }
        return width;
    }, [drawBudget?.draws, toggleArray, isAdmin, anyAcquisitionAmount]);

    const onConfirmFeedback = async (category: number, type: number, comment: any) => {
        await drawApi.addFeedback({
            drawBudgetId: drawBudget?.id,
            feedbackCategory: category,
            feedbackType: type,
            comment: comment,
        });
        set_isFeedbackOpen(false);
    };

    const onCancelFeedback = () => {
        set_isFeedbackOpen(false);
    };

    const navigateToPreviousLoanPage = (page: string, section: string) => {
        if (!drawBudget) throw new Error('Could not find the draw budget.');

        const url = '/loans/' + page;
        dispatch(
            loanActions.setEditStdLoanModal({
                editStdLoanModal: {
                    isOpen: true,
                    loanId: drawBudget.loanId,
                    showInsurance: true,
                },
            }) as never
        );
        if (section != null && section !== undefined && section !== '') {
            window.location.href = url + '/' + section;
        } else {
            window.location.href = url;
        }
    };

    const onNavigationBackButtonClick = () => {
        if (!drawBudget) throw new Error('Could not find the draw budget.');

        switch (originPage) {
            case 'dashboard':
                window.location.href = '/draw/dashboard';
                break;
            case 'outstanding':
            case 'approved':
            case 'repaid':
                navigateToPreviousLoanPage(originPage, originSection);
                break;
            default:
                throw new Error('Could not navigate to the previous page');
        }
    };

    if (
        drawBudget &&
        drawBudget.loanStatusId !== LoanStatusType.Approved &&
        drawBudget.loanStatusId !== LoanStatusType.Outstanding &&
        ((isAdmin && drawBudget.loanStatusId !== LoanStatusType.Repaid) || !isAdmin)
    ) {
        return (
            <h2
                style={{
                    backgroundColor: 'white',
                    textAlign: 'center',
                    padding: 100,
                }}
            >
                {isAdmin
                    ? 'Unable to view Budget because of the current status of the Loan.'
                    : 'Unable to view Budget - please contact BFI if you believe this is in error.'}
            </h2>
        );
    }

    const mobileScrollChevrons = (
        <>
            <div
                className='mobile-scroll-icon-row'
                style={{ left: chevronsCenteredOffset + columnWidth(20) }}
            >
                <div className='mobile-scroll-icon-set'>
                    <button onClick={handlePreviousSecond}>
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <button onClick={handleNextSecond}>
                        <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                </div>

                <div className='mobile-scroll-icon-set'>
                    <button
                        onClick={handlePreviousThird}
                        disabled={!numberOfDraws}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                    <button
                        onClick={handleNextThird}
                        disabled={!numberOfDraws}
                    >
                        <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                </div>
            </div>
        </>
    );

    if (drawBudget?.hasEntityUser === false) {
        return (
            <Panel
                theme={PanelTheme.Red}
                borderStyle={PanelBorderStyle.Solid}
            >
                <div style={{ paddingLeft: 100, height: 800 }}>
                    <h1 style={{ paddingTop: 50 }}>The entity associated with this draw has no linked user.</h1>
                    <h6 style={{ paddingTop: 20 }}>Please connect a user to create a budget and start draws.</h6>
                    <DrawHeader
                        drawBudget={drawBudget}
                        set_drawBudget={set_drawBudget}
                        isAdmin={false}
                        anyAcquisitionAmount={anyAcquisitionAmount}
                        equityAmount={equityAmount}
                        isEquityMet={isEquityMet}
                        borrowerName={borrowerName}
                        latestDrawFundedTotal={latestDrawFundedTotal}
                        latestDrawBorrowerRequestTotal={latestDrawBorrowerRequestTotal}
                        latestDrawCreatedDate={latestDrawCreatedDate}
                        applicationInsights={applicationInsights}
                        showFeedback={set_isFeedbackOpen}
                        finalizeSnackbar={finalizeSnackbar}
                        isReadOnly={isReadOnly}
                    />
                </div>
            </Panel>
        );
    }
    return (
        <div className='draw-container'>
            <div>
                {isAdmin && (
                    <div style={{ marginBottom: 10 }}>
                        <span className='icon'>
                            <FontAwesomeIcon icon={faChevronLeft} />
                        </span>
                        &nbsp;
                        <button
                            onClick={onNavigationBackButtonClick}
                            className='link'
                            style={{ marginBottom: 4 }}
                        >
                            <i>{backButtonText}</i>
                        </button>
                        &nbsp;&nbsp;
                    </div>
                )}
                <DrawHeader
                    drawBudget={drawBudget}
                    set_drawBudget={set_drawBudget}
                    isAdmin={isAdmin}
                    anyAcquisitionAmount={anyAcquisitionAmount}
                    equityAmount={equityAmount}
                    isEquityMet={isEquityMet}
                    borrowerName={borrowerName}
                    latestDrawFundedTotal={latestDrawFundedTotal}
                    latestDrawBorrowerRequestTotal={latestDrawBorrowerRequestTotal}
                    latestDrawCreatedDate={latestDrawCreatedDate}
                    applicationInsights={applicationInsights}
                    showFeedback={set_isFeedbackOpen}
                    finalizeSnackbar={finalizeSnackbar}
                    isReadOnly={isReadOnly}
                />
                {showMobileScroll && mobileScrollChevrons}
                <DrawGrid
                    dataGridRef={dataGridRef}
                    applicationInsights={applicationInsights}
                    drawBudgetId={drawBudget?.id ?? 0}
                    loanTypeId={drawBudget?.id ? drawBudget?.loanTypeId : undefined}
                    draws={drawBudget?.draws}
                    noDraws={noDraws}
                    equityRequirement={drawBudget?.equityRequirement ?? 0}
                    drawColumns={drawColumns}
                    drawTotalItems={drawTotalItems}
                    currentApproval={drawBudget?.currentApproval}
                    set_categories={set_categories}
                    set_currentApproval={set_currentApproval}
                    set_equityAmount={set_equityAmount}
                    set_isEquityMet={set_isEquityMet}
                    set_totalFunded={set_totalFunded}
                    set_latestDrawFundedTotal={set_latestDrawFundedTotal}
                    set_latestDrawBorrowerRequestTotal={set_latestDrawBorrowerRequestTotal}
                    set_latestDrawCreatedDate={set_latestDrawCreatedDate}
                    set_isSnackbarOpen={set_isSnackbarOpen}
                    set_snackbarMessage={set_snackbarMessage}
                    isAdmin={isAdmin}
                    anyAcquisitionAmount={anyAcquisitionAmount}
                    tableWidth={calculateGridWidth()}
                    mobileScreen={mobileScreen}
                    mobileScrollData={mobileScrollData}
                    isReadOnly={isReadOnly}
                />
            </div>
            <DrawButtonGroup
                drawBudget={drawBudget}
                set_drawBudget={set_drawBudget}
                applicationInsights={applicationInsights}
                isAdmin={isAdmin}
                isEquityMet={isEquityMet}
                isInspectionsEnabled={isInspectionsEnabled}
                borrowerName={borrowerName}
                mobileScreen={mobileScreen}
                categories={categories}
                isReadOnly={isReadOnly}
            />
            <BfiSnackbar
                open={isSnackbarOpen}
                message={snackbarMessage}
                variant='warning'
                handleClose={() => set_isSnackbarOpen(false)}
            />
            <LoadingModal
                open={isLoading}
                message='Loading...'
            />{' '}
            {checkApplyV2Enabled() ? (
                <FeedbackModalV2
                    isOpen={isFeedbackOpen}
                    confirmFeedbackCallback={onConfirmFeedback}
                    cancelFeedbackCallback={onCancelFeedback}
                ></FeedbackModalV2>
            ) : (
                <FeedbackModal
                    isOpen={isFeedbackOpen}
                    confirmFeedbackCallback={onConfirmFeedback}
                    cancelFeedbackCallback={onCancelFeedback}
                ></FeedbackModal>
            )}
        </div>
    );
};
